import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { notificationsApi, formatApiError } from '../lib/api';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Bell, BellOff, Heart, ShoppingBag, Shield, CheckCheck, 
  Loader2, Package, ArrowRight, Trash2 
} from 'lucide-react';

const typeConfig = {
  new_listing: { icon: Package, color: 'text-primary', bg: 'bg-primary/10', label: 'New Listing' },
  favourite: { icon: Heart, color: 'text-[#E53935]', bg: 'bg-red-50', label: 'Saved' },
  admin: { icon: Shield, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Admin' },
  general: { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-100', label: 'General' }
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { refreshNotifCount } = useNotifications();
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [isAuthenticated, navigate]);

  const fetchNotifications = async () => {
    try {
      const { data } = await notificationsApi.getAll();
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      refreshNotifCount();
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      refreshNotifCount();
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) handleMarkRead(notif.id);
    if (notif.listing_id) {
      navigate(`/listing/${notif.listing_id}`);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen py-8" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-3xl font-bold flex items-center gap-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <Bell className="h-7 w-7 text-primary" />
              Notifications
            </h1>
            {unreadCount > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleMarkAllRead}
              className="gap-2"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map((notif) => {
              const config = typeConfig[notif.type] || typeConfig.general;
              const Icon = config.icon;
              
              return (
                <Card 
                  key={notif.id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                    !notif.read ? 'border-primary/30 bg-primary/[0.02]' : 'opacity-75'
                  }`}
                  onClick={() => handleClick(notif)}
                  data-testid={`notification-${notif.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-5 w-5 ${config.color}`} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`font-medium text-sm ${!notif.read ? 'text-gray-900' : 'text-gray-500'}`}>
                            {notif.title}
                          </p>
                          {!notif.read && (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2">{notif.message}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {config.label}
                          </Badge>
                          <span className="text-xs text-gray-400">{formatTime(notif.created_at)}</span>
                        </div>
                      </div>

                      {/* Arrow for clickable ones */}
                      {notif.listing_id && (
                        <ArrowRight className="h-4 w-4 text-gray-300 flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <BellOff className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No notifications yet</p>
            <p className="text-sm text-gray-400">
              You'll be notified about new listings, saved items, and messages
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
